import { ProgressPlanTool, RenderPlanTool } from './PlanTool.render.tsx';
import { type PlanInput, PlanInputSchema, type PlanOutput } from './schema.ts';
import { ShowDashboardTool } from '#plugin-chat/components/tools/show-dashboard/ShowDashboardTool.tsx';
import { TransformDatasetTool } from '#plugin-chat/components/tools/transform-dataset/TransformDatasetTool.tsx';
import { ToolGuidance } from '#plugin-chat/lib/instructions/components.tsx';
import { renderInstructions } from '#plugin-chat/lib/instructions/reconciler.ts';
import { createTool } from '#plugin-chat/lib/tool/create-tool.tsx';
import type { ToolConfig } from '#plugin-chat/lib/tool/create-tool.types.ts';

/**
 * Structured planning tool. Agents call this before any multi-step workflow so the
 * plan is expressed as validated tool-call fields (summary, final grain, expected
 * output, ordered steps, assumptions) rather than JSON-shaped prose narration in a
 * regular assistant message.
 */
export const PlanTool = createTool('plan', () => ({
  instructions: (
    <ToolGuidance
      tool={PlanTool}
      whenNotToUse={[
        'Trivial single-call answers (one lookup, one clarifying question, a single dashboard of already-staged data).',
        `Questions a single ${TransformDatasetTool.mention} or ${ShowDashboardTool.mention} call can answer on its own.`,
      ]}
      whenToUse={[
        'Any question that will require two or more tool calls to answer.',
        'Ambiguous or multi-step requests where stating the approach up front lets the user verify intent before data fetches start.',
      ]}
    >
      <header>Procedure</header>
      <ol>
        <li>
          Call this tool FIRST, before any data-fetching or processing tools. A plan after the fact is a failure mode.
        </li>
        <li>
          Each step <code>description</code> must be plain English — describe <em>what</em> the step does and
          <em>why</em>, never the tool arguments. The <code>tool</code> field is the only place a tool name belongs.
        </li>
        <li>
          Populate <code>finalGrain</code> and <code>expectedOutput</code> on every call. The plan must collapse raw
          data down to <code>finalGrain</code>, and the last step typically produces <code>expectedOutput</code> via
          {ShowDashboardTool.mention}.
        </li>
        <li>
          Surface assumptions you are making (entity names, time ranges, unit conventions, categorical encodings) in
          <code>assumptions</code> instead of guessing silently.
        </li>
        <li>
          After this tool returns, execute the plan. Do not restate or re-narrate it in a normal assistant message — the
          tool result speaks for itself.
        </li>
      </ol>
    </ToolGuidance>
  ),
  config: {
    parameters: PlanInputSchema,
    render: RenderPlanTool,
    progress: ProgressPlanTool,
    description: (
      <p>
        Declare the multi-step plan for answering the user's question. Call this before any other tool when the answer
        requires two or more tool calls.
      </p>
    ),
    execute: async (args): Promise<PlanOutput> => ({
      success: true,
      message: renderInstructions(
        <>
          <strong>The {PlanTool.mention} tool has prepared an execution plan:</strong>
          <p>{args.summary}</p>
          <header>Final grain</header>
          <p>{args.finalGrain}</p>
          <header>Expected output</header>
          <p>{args.expectedOutput}</p>
          <header>Steps</header>
          <ol>
            {args.steps.map((step) => (
              <li key={step.description}>
                {step.description}
                {step.tool ? (
                  <>
                    (via <code>{step.tool}</code>)
                  </>
                ) : null}
              </li>
            ))}
          </ol>
          {args.assumptions?.length ? (
            <>
              <header>Assumptions</header>
              <ul>
                {args.assumptions.map((assumption) => (
                  <li key={assumption}>{assumption}</li>
                ))}
              </ul>
            </>
          ) : null}
        </>,
      ),
    }),
  } satisfies ToolConfig<PlanInput, PlanOutput>,
}));
